import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

import { cn } from '@apps/web/lib/utils';

function Popover({
    ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
    return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({
    ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
    return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
    className,
    align = 'center',
    sideOffset = 4,
    container,
    onWheel,
    onTouchMove,
    ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content> & {
    container?: HTMLElement | null;
}) {
    // A Radix `Dialog` locks scrolling with `react-remove-scroll`, which
    // cancels wheel and touch scrolling for every target that is not inside
    // the dialog content. Popover content is portaled to `document.body`, so
    // scrollable areas inside a popover (for example a combobox list) cannot
    // be scrolled while a dialog is open. Keeping these events from reaching
    // the document-level scroll lock restores scrolling; `overscroll-contain`
    // keeps it from chaining to the page behind the popover.
    return (
        <PopoverPrimitive.Portal container={container}>
            <PopoverPrimitive.Content
                data-slot="popover-content"
                align={align}
                sideOffset={sideOffset}
                className={cn(
                    'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-[calc(100dvh-2rem)] w-72 max-w-[calc(100vw-2rem)] origin-(--radix-popover-content-transform-origin) overflow-y-auto overscroll-contain rounded-md border p-4 shadow-md outline-hidden',
                    className,
                )}
                onWheel={(event) => {
                    event.stopPropagation();
                    onWheel?.(event);
                }}
                onTouchMove={(event) => {
                    event.stopPropagation();
                    onTouchMove?.(event);
                }}
                {...props}
            />
        </PopoverPrimitive.Portal>
    );
}

function PopoverAnchor({
    ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
    return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger };
